import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const phases = [
  {
    n: "01",
    title: "Slow the cycle down",
    text: "In the aftermath of betrayal, conversations can swing between anger, grief, fear, guilt and defensiveness. Therapy creates a structured space to stabilize before deciding anything.",
  },
  {
    n: "02",
    title: "Understand what happened",
    text: "Not to excuse it — but to make sense of it. Couples need an honest account of what occurred and what it meant before repair becomes possible.",
  },
  {
    n: "03",
    title: "Address the impact of betrayal",
    text: "Infidelity can change the way you see your partner, your relationship and even yourself. The hurt partner's experience is given real weight, not rushed past.",
  },
  {
    n: "04",
    title: "Determine what rebuilding requires",
    text: "Trust is rebuilt through consistent, observable change over time — not promises. Together we define what that would actually look like for your relationship.",
  },
];

const Infidelity = () => {
  usePageMeta(
    "Infidelity Counseling & Rebuilding Trust in California | Holistic Healing Psychotherapy",
    "California couples therapy for infidelity and betrayal trauma. Structured, nonjudgmental support for rebuilding trust — or deciding what comes next."
  );

  return (
    <div data-testid="infidelity-page">
      <section className="bg-forest-950 pt-16 pb-20 lg:pt-28 lg:pb-28 relative overflow-hidden">
        <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-gold-500/8 blur-3xl pointer-events-none" />
        <div className="max-w-4xl mx-auto px-5 sm:px-8 relative">
          <FadeIn>
            <p className="eyebrow mb-6" data-testid="infidelity-eyebrow">Infidelity & Rebuilding Trust</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-cream-50" data-testid="infidelity-headline">
              When trust has been <em className="text-gold-400">broken</em>
            </h1>
            <p className="mt-7 text-base sm:text-lg text-cream-50/70 leading-relaxed max-w-2xl" data-testid="infidelity-intro">
              Infidelity can change the way you see your partner, your relationship and even yourself. Couples may
              find themselves moving between anger, grief, fear, guilt, defensiveness and a desperate desire to know
              whether the relationship can survive.
            </p>
            <p className="mt-4 text-base sm:text-lg text-cream-50/70 leading-relaxed max-w-2xl">
              Therapy can provide a structured space to slow the cycle down, understand what happened, address the
              impact of betrayal and determine what rebuilding trust would actually require.
            </p>
            <Link
              to="/contact"
              data-testid="infidelity-schedule-cta"
              className="inline-flex items-center gap-2 mt-9 rounded-full bg-cream-50 text-forest-900 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-white"
            >
              Schedule a Consultation <ArrowRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="The Work of Repair" title="What rebuilding trust actually involves." />
          <div className="mt-14 grid md:grid-cols-2 gap-x-16">
            {phases.map((p, i) => (
              <FadeIn key={p.n} delay={i * 0.08}>
                <div className="group py-8 border-t border-forest-800/10 hover:border-gold-500/50 transition-colors duration-500" data-testid={`infidelity-phase-${p.n}`}>
                  <div className="flex gap-6">
                    <span className="mt-3 w-3 h-3 rotate-45 bg-gold-500 group-hover:bg-gold-400 transition-colors duration-500 shrink-0" aria-hidden="true" />
                    <div>
                      <h3 className="font-serif text-2xl text-forest-800 mb-3">{p.title}</h3>
                      <p className="text-base text-sage leading-relaxed">{p.text}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-forest-900 py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 space-y-12">
          <FadeIn>
            <blockquote data-testid="infidelity-perel-quote-1">
              <p className="font-serif italic text-2xl sm:text-3xl text-cream-50 leading-snug">
                "Love is messy; infidelity more so. But it is also a window, like none other, into the crevices of
                the human heart."
              </p>
              <cite className="not-italic block mt-4 text-xs uppercase tracking-[0.25em] text-gold-400">— Esther Perel, The State of Affairs</cite>
            </blockquote>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-100 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <FadeIn>
            <p className="font-serif text-2xl sm:text-3xl lg:text-4xl text-forest-800 leading-snug" data-testid="infidelity-close">
              Whether you are trying to repair the relationship, trying to understand what happened, or simply trying
              to make an informed decision about what comes next —{" "}
              <em className="text-gold-600">you do not have to navigate the process alone.</em>
            </p>
            <Link
              to="/contact"
              data-testid="infidelity-bottom-cta"
              className="inline-flex items-center gap-2 mt-10 rounded-full bg-forest-800 text-cream-50 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-forest-900"
            >
              Schedule a Consultation <ArrowRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <CTASection
        title="Betrayal trauma support, without judgment."
        copy="Rebuilding trust is possible for many couples — and clarity is possible for all of them."
      />
    </div>
  );
};

export default Infidelity;
